import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../components/Navbar';
import LaunchSpecialTimer, { getTimeRemaining } from '../components/LaunchSpecialTimer';
import { APP_STORE_URL, PLAY_STORE_URL, getDownloadUrl } from '../storeLinks';

function LandingPage() {
  const { t } = useTranslation();
  const downloadUrl = getDownloadUrl();
  const specialActive = getTimeRemaining() !== null;
  const navLinks = [
    { label: t('nav.journal'), to: '/blog' },
    { label: t('nav.pricing'), to: '/pricing' },
    { label: t('nav.download'), to: downloadUrl, external: true, cta: true },
  ];

  return (
    <div className="landing">
      <Navbar links={navLinks} />

      {/* Hero Section */}
      <section className="hero">
        <div className="container">
          <div className="hero-split">
            {/* Left — text + CTAs */}
            <div className="hero-left">
              <img src="/logo512.png" alt={t('landing.hero.logoAlt')} className="logo-image" />
              <h1>10 Thirty</h1>
              <p className="tagline">{t('landing.hero.tagline')}</p>
              <p className="subtitle">
                {t('landing.hero.subtitle')}
              </p>
              <div className="hero-stores">
                <a href={APP_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
                  <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                  </svg>
                  <div className="store-text">
                    <small>{t('landing.stores.appStoreSmall')}</small>
                    <span>{t('landing.stores.appStoreLine')}</span>
                  </div>
                </a>
                <a href={PLAY_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
                  <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
                  </svg>
                  <div className="store-text">
                    <small>{t('landing.stores.playStoreSmall')}</small>
                    <span>{t('landing.stores.playStoreLine')}</span>
                  </div>
                </a>
              </div>
            </div>

            {/* Right — screenshot */}
            <div className="hero-right">
              <img
                src="/hero-screenshot.jpeg"
                alt={t('landing.hero.screenshotAlt')}
                className="hero-screenshot"
              />
              <p className="hero-screenshot-caption">
                {t('landing.hero.screenshotCaption')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Launch Special */}
      {specialActive && (
        <section className="landing-special">
          <div className="container">
            <div className="landing-special-card">
              <div className="landing-special-copy">
                <span className="launch-special-banner">{t('pricing.launchLabel')}</span>
                <h2>{t('landing.special.heading')}</h2>
                <p>{t('landing.special.body')}</p>
                <Link to="/pricing" className="btn-primary landing-special-cta">
                  {t('landing.special.cta')}
                </Link>
              </div>
              <div className="landing-special-clockside">
                <LaunchSpecialTimer hideWhenEnded />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Trust Section */}
      <section className="proof">
        <div className="container">
          <h2 className="proof-heading">{t('landing.trust.heading')}</h2>
          <p className="proof-subheading">
            {t('landing.trust.subheading')}
          </p>
          <div className="trust-points">
            <div className="trust-point">
              <div className="trust-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <div>
                <h4>{t('landing.trust.point1Title')}</h4>
                <p>{t('landing.trust.point1Body')}</p>
              </div>
            </div>
            <div className="trust-point">
              <div className="trust-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <path d="M8 12h8M12 8v8"/>
                </svg>
              </div>
              <div>
                <h4>{t('landing.trust.point2Title')}</h4>
                <p>{t('landing.trust.point2Body')}</p>
              </div>
            </div>
            <div className="trust-point">
              <div className="trust-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <div>
                <h4>{t('landing.trust.point3Title')}</h4>
                <p>{t('landing.trust.point3Body')}</p>
              </div>
            </div>
          </div>
          <p className="proof-conclusion">
            {t('landing.trust.conclusion')}
          </p>
        </div>
      </section>

      {/* Who It's For Section */}
      <section className="philosophy">
        <div className="container">
          <h2>{t('landing.audience.heading')}</h2>
          <div className="philosophy-content">
            <div className="philosophy-block">
              <h3>{t('landing.audience.legalTitle')}</h3>
              <p>
                {t('landing.audience.legalBody')}
              </p>
            </div>
            <div className="philosophy-block">
              <h3>{t('landing.audience.financeTitle')}</h3>
              <p>
                {t('landing.audience.financeBody')}
              </p>
            </div>
            <div className="philosophy-block highlight">
              <h3>{t('landing.audience.execTitle')}</h3>
              <p>
                {t('landing.audience.execBody')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Three Pillars */}
      <section className="pillars">
        <div className="container">
          <h2>{t('landing.pillars.heading')}</h2>
          <div className="pillars-grid">
            <div className="pillar">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                  <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
              </div>
              <h3>{t('landing.pillars.encryptedTitle')}</h3>
              <p className="pillar-tagline">{t('landing.pillars.encryptedTagline')}</p>
              <p>
                {t('landing.pillars.encryptedBody')}
              </p>
            </div>
            <div className="pillar">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
                  <path d="M9 12l2 2 4-4"/>
                </svg>
              </div>
              <h3>{t('landing.pillars.consentTitle')}</h3>
              <p className="pillar-tagline">{t('landing.pillars.consentTagline')}</p>
              <p>
                {t('landing.pillars.consentBody')}
              </p>
            </div>
            <div className="pillar">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10"/>
                  <polyline points="12,6 12,12 16,14"/>
                </svg>
              </div>
              <h3>{t('landing.pillars.ephemeralTitle')}</h3>
              <p className="pillar-tagline">{t('landing.pillars.ephemeralTagline')}</p>
              <p>
                {t('landing.pillars.ephemeralBody')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Beyond the tunnel — extended primitives */}
      <section className="pillars pillars-compact">
        <div className="container">
          <h2>{t('landing.beyond.heading')}</h2>
          <div className="pillars-grid pillars-grid-2">
            <div className="pillar">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M12 2l3 6 6 1-4.5 4 1 6-5.5-3-5.5 3 1-6L3 9l6-1z" />
                </svg>
              </div>
              <h3>{t('landing.beyond.sosTitle')}</h3>
              <p className="pillar-tagline">{t('landing.beyond.sosTagline')}</p>
              <p>
                {t('landing.beyond.sosBody')}
              </p>
            </div>
            <div className="pillar">
              <div className="pillar-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M16 4l4 4-8 12-8-4 8-12 4 0z" />
                  <path d="M12 8l0 8" />
                </svg>
              </div>
              <h3>{t('landing.beyond.pigeonsTitle')}</h3>
              <p className="pillar-tagline">{t('landing.beyond.pigeonsTagline')}</p>
              <p>
                {t('landing.beyond.pigeonsBody')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="how-it-works">
        <div className="container">
          <h2>{t('landing.how.heading')}</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h4>{t('landing.how.step1Title')}</h4>
              <p>{t('landing.how.step1Body')}</p>
            </div>
            <div className="step">
              <div className="step-number">2</div>
              <h4>{t('landing.how.step2Title')}</h4>
              <p>{t('landing.how.step2Body')}</p>
            </div>
            <div className="step">
              <div className="step-number">3</div>
              <h4>{t('landing.how.step3Title')}</h4>
              <p>{t('landing.how.step3Body')}</p>
            </div>
            <div className="step">
              <div className="step-number">4</div>
              <h4>{t('landing.how.step4Title')}</h4>
              <p>{t('landing.how.step4Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Ubuntu Program */}
      <section className="referral-section">
        <div className="container">
          <div className="referral-card">
            <div className="referral-content">
              <div className="referral-badge">{t('landing.ubuntu.badge')}</div>
              <h2>{t('landing.ubuntu.heading')}</h2>
              <p className="referral-desc">
                {t('landing.ubuntu.intro')}
              </p>

              <div className="ubuntu-tracks">
                <div className="ubuntu-track">
                  <div className="ubuntu-track-header">
                    <span className="ubuntu-track-icon">🤝</span>
                    <h3>{t('landing.ubuntu.track1Title')}</h3>
                  </div>
                  <p>{t('landing.ubuntu.track1Body')}</p>
                  <div className="ubuntu-highlight">{t('landing.ubuntu.track1Highlight')}</div>
                </div>

                <div className="ubuntu-track">
                  <div className="ubuntu-track-header">
                    <span className="ubuntu-track-icon">🐛</span>
                    <h3>{t('landing.ubuntu.track2Title')}</h3>
                  </div>
                  <p>{t('landing.ubuntu.track2Body')}</p>
                  <div className="ubuntu-highlight">{t('landing.ubuntu.track2Highlight')}</div>
                </div>
              </div>

              <div className="referral-steps">
                <div className="referral-step">
                  <div className="referral-step-num">1</div>
                  <div>
                    <strong>{t('landing.ubuntu.step1Title')}</strong>
                    <span>{t('landing.ubuntu.step1Body')}</span>
                  </div>
                </div>
                <div className="referral-step">
                  <div className="referral-step-num">2</div>
                  <div>
                    <strong>{t('landing.ubuntu.step2Title')}</strong>
                    <span>{t('landing.ubuntu.step2Body')}</span>
                  </div>
                </div>
                <div className="referral-step">
                  <div className="referral-step-num">3</div>
                  <div>
                    <strong>{t('landing.ubuntu.step3Title')}</strong>
                    <span>{t('landing.ubuntu.step3Body')}</span>
                  </div>
                </div>
              </div>

              <a href={downloadUrl} className="btn-primary referral-cta" target="_blank" rel="noopener noreferrer">
                {t('landing.ubuntu.cta')}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Download Section */}
      <section className="download">
        <div className="container">
          <h2>{t('landing.download.heading')}</h2>
          <p className="download-subtitle">{t('landing.download.subtitle')}</p>
          <div className="stores">
            <a href={APP_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
              <div className="store-text">
                <small>{t('landing.stores.appStoreSmall')}</small>
                <span>{t('landing.stores.appStoreLine')}</span>
              </div>
            </a>
            <a href={PLAY_STORE_URL} className="store-btn" target="_blank" rel="noopener noreferrer">
              <svg className="store-icon" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3,20.5V3.5C3,2.91 3.34,2.39 3.84,2.15L13.69,12L3.84,21.85C3.34,21.6 3,21.09 3,20.5M16.81,15.12L6.05,21.34L14.54,12.85L16.81,15.12M20.16,10.81C20.5,11.08 20.75,11.5 20.75,12C20.75,12.5 20.53,12.9 20.18,13.18L17.89,14.5L15.39,12L17.89,9.5L20.16,10.81M6.05,2.66L16.81,8.88L14.54,11.15L6.05,2.66Z"/>
              </svg>
              <div className="store-text">
                <small>{t('landing.stores.playStoreSmall')}</small>
                <span>{t('landing.stores.playStoreLine')}</span>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer>
        <div className="container">
          <div className="footer-brand">
            <img src="/logo192.png" alt={t('landing.hero.logoAlt')} className="footer-logo" />
            <span>10 Thirty</span>
          </div>
          <p>
            {t('landing.footer.copyright', { year: new Date().getFullYear() })}{' '}
            {t('landing.footer.broughtToYou')}{' '}
            <a href="https://www.callitservices.co.za" target="_blank" rel="noopener noreferrer">{t('landing.footer.callItServices')}</a>
          </p>
          <div className="links">
            <Link to="/blog">{t('landing.footer.linkJournal')}</Link>
            <Link to="/pricing">{t('landing.footer.linkPricing')}</Link>
            <Link to="/pitch">{t('landing.footer.linkInvestors')}</Link>
            <Link to="/privacy">{t('landing.footer.linkPrivacy')}</Link>
            <Link to="/terms">{t('landing.footer.linkTerms')}</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
